/**
 * What the router's catch-all route should do with a path nothing else matched.
 *
 * Extracted from `LocaleRedirect` in router.tsx so the decision can be tested
 * without mounting the whole route tree.
 *
 * The catch-all used to redirect unconditionally to `/en` + the path with any
 * leading `/en` segments stripped. For a path that ALREADY carried a locale and
 * still matched nothing — /en/no/such/route/exists/here/at-all — that target is
 * the current URL, so <Navigate> replaced the URL with itself and rendered
 * nothing: #root stayed empty and the index.html spinner never stopped. There
 * is no error to catch and no page to show, so RouteErrorBoundary's "Page not
 * found" state, which existed the whole time, was never reached.
 *
 * (The old `/^(\/en)+/` strip had a second bug of its own: it matched inside
 * /england, turning that into another redirect-to-itself.)
 */

// The locale registry, not '@/i18n' — this module is pure, and importing the
// i18n entry point would initialise i18next (and its HTTP backend) to read a
// list of strings.
import { localeCodes } from '@/locales';

export type CatchAllResolution =
  /** Nothing can be salvaged from this path — show the not-found screen. */
  | { kind: 'not-found' }
  /** The path is missing its locale prefix; send the browser to `to`. */
  | { kind: 'redirect'; to: string };

export function resolveCatchAll(pathname: string, search = '', hash = ''): CatchAllResolution {
  const first = pathname.split('/')[1] || '';
  // A locale prefix means the path was already resolved as far as it can be:
  // the route tree simply has nothing at it. Prefixing another /en would only
  // produce the same URL (or a longer nonsense one) and lose the visitor's
  // language on the error screen, which reads the locale straight off the path.
  if (localeCodes.includes(first)) return { kind: 'not-found' };
  return { kind: 'redirect', to: `/en${pathname === '/' ? '' : pathname}${search}${hash}` };
}
